import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import ErrorAlert from "components/ErrorAlert";
import CustomSelect from "components/CustomSelect";
import Table from "examples/Tables/Table";
import { decideLeaveRequestReq, getLeaveRequestsReq } from "services/api";
import usePermissions from "utils/usePermissions";
import { PERMISSIONS } from "utils/permissions";
import { customSelectStyles } from "layouts/reports/reports.constants";

const PAGE_SIZE = 10;
const DATE_FORMAT = "D MMM YYYY";

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Declined", value: "rejected" },
];

const TYPE_OPTIONS = [
  { label: "Leave & WFH", value: "" },
  { label: "Leave", value: "Leave" },
  { label: "WFH", value: "WFH" },
];

// The stored status stays "rejected"; people see "declined".
const STATUS_LABELS = { pending: "pending", approved: "approved", rejected: "declined" };
const STATUS_COLORS = { pending: "warning", approved: "success", rejected: "error" };

const columns = [
  { name: "requested_by", align: "left" },
  { name: "type", align: "left" },
  { name: "dates", align: "left" },
  { name: "reason", align: "left" },
  { name: "leave_balance", align: "left" },
  { name: "status", align: "center" },
  { name: "decided_by", align: "left" },
];
const columnsWithActions = [...columns, { name: "actions", align: "center" }];

const WEEKDAY_LABELS = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri" };

// Days in start..end that fall on one of the request's weekdays (a request
// without weekdays covers every day in its range).
const countDays = (start, end, weekdays) => {
  const wanted = weekdays ? weekdays.split(",").map(Number) : null;
  let count = 0;
  for (const day = moment(start); day.isSameOrBefore(moment(end)); day.add(1, "day")) {
    if (!wanted || wanted.includes(day.day())) count += 1;
  }
  return count;
};

const describeDays = ({ start_date, end_date, weekdays }) => {
  if (!weekdays) return null;
  const count = countDays(start_date, end_date, weekdays);
  const label = `${count} day${count === 1 ? "" : "s"}`;
  const list = weekdays.split(",");
  return start_date === end_date || list.length === Object.keys(WEEKDAY_LABELS).length
    ? label
    : `Only ${list.map((day) => WEEKDAY_LABELS[day]).join(", ")} · ${label}`;
};

const formatDates = (start, end) =>
  start === end
    ? moment(start).format(DATE_FORMAT)
    : `${moment(start).format(DATE_FORMAT)} – ${moment(end).format(DATE_FORMAT)}`;

const buildRow = (request, onDecide) => ({
  requested_by: (
    <SuiBox px={1} py={0.5}>
      <SuiTypography variant="button" fontWeight="medium">
        {request.user_name}
      </SuiTypography>
      <SuiTypography variant="caption" color="secondary" display="block">
        {moment.unix(request.created_at).format(`${DATE_FORMAT}, h:mm A`)}
      </SuiTypography>
    </SuiBox>
  ),
  type: (
    <SuiTypography variant="caption" fontWeight="medium">
      {request.type}
    </SuiTypography>
  ),
  dates: (
    <SuiBox>
      <SuiTypography variant="caption" fontWeight="medium" display="block">
        {formatDates(request.start_date, request.end_date)}
      </SuiTypography>
      {describeDays(request) && (
        <SuiTypography variant="caption" color="secondary" display="block">
          {describeDays(request)}
        </SuiTypography>
      )}
    </SuiBox>
  ),
  // Only pending Leave requests carry a summary (it is worked out as of today).
  leave_balance: request.leave_summary ? (
    <SuiBox sx={{ minWidth: 170 }}>
      <SuiTypography variant="caption" fontWeight="medium" display="block">
        {request.leave_summary.balance >= 0
          ? `${request.leave_summary.balance} days left`
          : `${-request.leave_summary.balance} days over`}
      </SuiTypography>
      <SuiTypography variant="caption" color="secondary" display="block">
        Accrued {request.leave_summary.accrued} · Taken {request.leave_summary.taken}
      </SuiTypography>
      <SuiTypography variant="caption" color="secondary" display="block">
        Joined {moment(request.leave_summary.joined_on).format(DATE_FORMAT)}
      </SuiTypography>
      {request.leave_summary.on_probation && (
        <SuiTypography variant="caption" color="warning" display="block">
          On probation until {moment(request.leave_summary.probation_end).format(DATE_FORMAT)}
        </SuiTypography>
      )}
    </SuiBox>
  ) : (
    <SuiTypography variant="caption" color="secondary">
      —
    </SuiTypography>
  ),
  reason: (
    <SuiBox sx={{ maxWidth: 280, whiteSpace: "normal", wordBreak: "break-word" }}>
      <SuiTypography variant="caption">{request.reason}</SuiTypography>
    </SuiBox>
  ),
  status: (
    <Chip
      size="small"
      label={STATUS_LABELS[request.status] || request.status}
      color={STATUS_COLORS[request.status]}
      sx={{ textTransform: "capitalize" }}
    />
  ),
  decided_by: request.decided_by_name ? (
    <SuiBox sx={{ maxWidth: 240, whiteSpace: "normal", wordBreak: "break-word" }}>
      <SuiTypography variant="caption" fontWeight="medium" display="block">
        {request.decided_by_name}
      </SuiTypography>
      <SuiTypography variant="caption" color="secondary" display="block">
        {moment.unix(request.decided_at).format(`${DATE_FORMAT}, h:mm A`)}
      </SuiTypography>
      {request.decision_reason && (
        <SuiTypography variant="caption" color="text" display="block">
          “{request.decision_reason}”
        </SuiTypography>
      )}
    </SuiBox>
  ) : (
    <SuiTypography variant="caption" color="secondary">
      —
    </SuiTypography>
  ),
  actions:
    onDecide && request.status === "pending" ? (
      <SuiBox display="flex" gap={1} justifyContent="center">
        <SuiButton size="small" color="success" onClick={() => onDecide(request, "approved")}>
          Approve
        </SuiButton>
        <SuiButton size="small" color="error" onClick={() => onDecide(request, "rejected")}>
          Decline
        </SuiButton>
      </SuiBox>
    ) : null,
});

function LeaveRequests() {
  const {
    auth: { admin, team },
  } = useSelector((state) => state);

  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [type, setType] = useState(TYPE_OPTIONS[0]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const latestRequest = useRef(0);
  const { can } = usePermissions();
  // Super Admin, Tech Lead and HR may approve or decline pending requests here.
  const canDecide = can(PERMISSIONS.REQUESTS_DECIDE);
  // The request being approved or declined, and what the admin typed as a reason.
  const [pendingDecision, setPendingDecision] = useState(null);
  const [decisionReason, setDecisionReason] = useState("");
  const [isDeciding, setIsDeciding] = useState(false);
  // A ref, not state: a second click can arrive before the re-render that disables Confirm.
  const decidingRef = useRef(false);
  const [notice, setNotice] = useState(null);

  // Let the success message fade on its own.
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // Wait for a pause in typing before querying, and go back to page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchRequests = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setIsLoading(true);
    try {
      const { data } = await getLeaveRequestsReq({
        admin_id: admin.id,
        team_id: team.id,
        params: {
          pageNumber: page,
          pageLimit: PAGE_SIZE,
          ...(status.value ? { status: status.value } : {}),
          ...(type.value ? { type: type.value } : {}),
          ...(search ? { search } : {}),
        },
      });
      // Ignore an answer that a newer filter change has already replaced.
      if (requestId !== latestRequest.current) return;
      setRequests(data.requests);
      setTotal(data.total);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      setErrorMessage(err.response?.data || "Failed to load requests");
    }
    if (requestId === latestRequest.current) setIsLoading(false);
  }, [admin.id, team.id, page, status, type, search]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openDecision = (request, decision) => {
    setDecisionReason("");
    setPendingDecision({ request, decision });
  };

  const confirmDecision = async () => {
    if (decidingRef.current) return;
    decidingRef.current = true;
    setIsDeciding(true);
    try {
      await decideLeaveRequestReq({
        admin_id: admin.id,
        team_id: team.id,
        request_id: pendingDecision.request.id,
        decision: pendingDecision.decision,
        reason: pendingDecision.decision === "rejected" ? decisionReason.trim() : undefined,
      });
      setPendingDecision(null);
      setNotice(
        `${pendingDecision.request.user_name}'s ${pendingDecision.request.type} request was ${
          STATUS_LABELS[pendingDecision.decision]
        }.`
      );
      await fetchRequests();
    } catch (err) {
      setPendingDecision(null);
      setErrorMessage(err.response?.data || "Failed to save the decision");
      // The list may be stale (someone decided it in Slack), so reload it.
      await fetchRequests();
    }
    decidingRef.current = false;
    setIsDeciding(false);
  };

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const hasFilters = Boolean(status.value || type.value || search);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {notice && !errorMessage && (
        <ErrorAlert color="success" message={notice} clearError={() => setNotice(null)} />
      )}
      {errorMessage && (
        <ErrorAlert
          color="error"
          message={typeof errorMessage === "string" ? errorMessage : "Failed to load requests"}
          clearError={() => setErrorMessage(null)}
        />
      )}
      <SuiBox py={3}>
        <Card>
          <SuiBox p={3} pb={2}>
            <SuiTypography variant="h6">Leave &amp; WFH requests</SuiTypography>
            <SuiTypography variant="caption" color="secondary">
              Requests made with /request in Slack.
              {canDecide
                ? " Pending ones can be approved or declined here or in Slack."
                : " Approve or decline them in Slack."}
            </SuiTypography>
          </SuiBox>
          <SuiBox display="flex" flexWrap="wrap" alignItems="center" px={3} pb={2}>
            <SuiBox pr={2} pb={1} sx={{ width: 190 }}>
              <CustomSelect
                customStyles={customSelectStyles}
                options={STATUS_OPTIONS}
                value={status}
                onChange={(option) => {
                  setStatus(option);
                  setPage(1);
                }}
              />
            </SuiBox>
            <SuiBox pr={2} pb={1} sx={{ width: 190 }}>
              <CustomSelect
                customStyles={customSelectStyles}
                options={TYPE_OPTIONS}
                value={type}
                onChange={(option) => {
                  setType(option);
                  setPage(1);
                }}
              />
            </SuiBox>
            <SuiBox pb={1} sx={{ width: 260 }}>
              <SuiInput
                placeholder="Search by name or reason…"
                icon={{ component: "search", direction: "left" }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </SuiBox>
          </SuiBox>
          <SuiBox
            sx={{
              "& .MuiTableRow-root:not(:last-child) td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            }}
          >
            {isLoading && requests.length === 0 ? (
              <SuiBox display="flex" justifyContent="center" p={4}>
                <CircularProgress color="dark" size={24} />
              </SuiBox>
            ) : requests.length > 0 ? (
              <SuiBox sx={{ opacity: isLoading ? 0.6 : 1 }}>
                <Table
                  columns={canDecide ? columnsWithActions : columns}
                  rows={requests.map((request) =>
                    buildRow(request, canDecide ? openDecision : null)
                  )}
                />
              </SuiBox>
            ) : (
              <SuiBox display="flex" justifyContent="center" p={4}>
                <SuiTypography variant="button" color="secondary">
                  {hasFilters ? "No requests match these filters." : "No requests yet."}
                </SuiTypography>
              </SuiBox>
            )}
          </SuiBox>
          {total > PAGE_SIZE && (
            <SuiBox display="flex" justifyContent="space-between" alignItems="center" px={3} py={2}>
              <SuiTypography variant="caption" color="secondary">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </SuiTypography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": { fontSize: 12 },
                  "& .Mui-selected": { bgcolor: "#17c1e8 !important", color: "#fff" },
                }}
              />
            </SuiBox>
          )}
        </Card>
      </SuiBox>
      <Dialog
        open={Boolean(pendingDecision)}
        onClose={() => !isDeciding && setPendingDecision(null)}
        fullWidth
        maxWidth="xs"
      >
        {pendingDecision && (
          <>
            <DialogTitle>
              {pendingDecision.decision === "approved" ? "Approve" : "Decline"}{" "}
              {pendingDecision.request.type} request?
            </DialogTitle>
            <DialogContent>
              <SuiTypography variant="button" display="block" mb={1}>
                {pendingDecision.request.user_name}:{" "}
                {formatDates(pendingDecision.request.start_date, pendingDecision.request.end_date)}
              </SuiTypography>
              <SuiTypography variant="caption" color="secondary" display="block" mb={2}>
                The card in Slack is updated and {pendingDecision.request.user_name} is messaged.
              </SuiTypography>
              {pendingDecision.decision === "rejected" && (
                <SuiInput
                  placeholder="Reason (optional)"
                  inputProps={{ "aria-label": "Reason for declining (optional)", maxLength: 1000 }}
                  multiline
                  rows={3}
                  value={decisionReason}
                  onChange={(e) => setDecisionReason(e.target.value)}
                />
              )}
            </DialogContent>
            <DialogActions>
              <SuiButton
                variant="text"
                color="secondary"
                disabled={isDeciding}
                onClick={() => setPendingDecision(null)}
              >
                Cancel
              </SuiButton>
              <SuiButton
                color={pendingDecision.decision === "approved" ? "success" : "error"}
                disabled={isDeciding}
                onClick={confirmDecision}
              >
                {pendingDecision.decision === "approved" ? "Approve" : "Decline"}
              </SuiButton>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default LeaveRequests;
